import React, { useState } from "react";
import { FaMapMarkerAlt, FaPhoneAlt, FaClock, FaCheck, FaTimes } from "react-icons/fa";
import Footer from "./Footer";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState({
    success: null,
    message: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResponse({ success: null, message: "" });

    try {
      const web3FormsUrl = "https://api.web3forms.com/submit";
      const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

      const res = await fetch(web3FormsUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          name: formData.name,
          email: formData.email,
          subject: formData.subject || "No subject",
          message: formData.message,
        }),
      });

      const data = await res.json();

      if (data.success) {
        setResponse({
          success: true,
          message: data.message || "Message sent successfully!",
        });
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        setResponse({
          success: false,
          message: data.message || "Failed to send message. Please try again.",
        });
      }
    } catch (error) {
      console.error("Web3Forms Error:", error);
      setResponse({
        success: false,
        message: "Network error. Please check your connection and try again.",
      });
    } finally {
      setLoading(false);
      setTimeout(() => setResponse({ success: null, message: "" }), 5000);
    }
  };

  return (
    <div className="bg-grey text-white min-h-screen">
      <div className="container mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold text-center mb-4">Get In Touch With Us</h2>
        <p className="text-gray-400 max-w-2xl text-center mx-auto mb-12">
          Have questions about our products or services? We're here to help! 
          Fill out the form or use our contact information below.
        </p>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Contact Information */}
          <div className="lg:w-1/2 space-y-8">
            <div className="bg-dark-grey p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <FaMapMarkerAlt className="text-orange text-xl mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium">Address</h4>
                    <p className="text-gray-400">Akoka Yaba, Lagos, Nigeria</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <FaPhoneAlt className="text-orange text-xl mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium">Phone</h4>
                    <p className="text-gray-400">+234 707 760 2469</p>
                    <p className="text-gray-400">+234 812 345 6789</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <FaClock className="text-orange text-xl mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium">Working Hours</h4>
                    <p className="text-gray-400">Monday-Friday: 9:00AM - 10:00PM</p>
                    <p className="text-gray-400">Saturday-Sunday: 9:00AM - 9:00PM</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:w-1/2">
            <div className="bg-dark-grey p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-6">Send Us a Message</h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium">Your Name*</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full bg-grey border border-gray-700 rounded-md p-3 focus:border-orange focus:ring-orange"
                      required
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium">Email Address*</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full bg-grey border border-gray-700 rounded-md p-3 focus:border-orange focus:ring-orange"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full bg-grey border border-gray-700 rounded-md p-3 focus:border-orange focus:ring-orange"
                    placeholder="Optional"
                  />
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium">Your Message*</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    className="w-full bg-grey border border-gray-700 rounded-md p-3 focus:border-orange focus:ring-orange"
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-3 px-4 rounded-md font-medium transition-colors ${
                    loading
                      ? "bg-gray-600 cursor-not-allowed"
                      : "bg-orange hover:bg-orange-dark"
                  }`}
                >
                  {loading ? "Sending Message..." : "Send Message"}
                </button>

                {response.message && (
                  <div
                    className={`p-3 rounded-md flex items-center gap-3 ${
                      response.success
                        ? "bg-green-900 text-green-100"
                        : "bg-red-900 text-red-100"
                    }`}
                  >
                    {response.success ? (
                      <FaCheck className="text-green-300" />
                    ) : (
                      <FaTimes className="text-red-300" />
                    )}
                    <span>{response.message}</span>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
